import React from 'react';
import {
    Button,
    Card,
    Descriptions,
    Popconfirm,
    Space,
    Tag,
    Typography,
} from 'antd';
import {
    DeleteOutlined,
    EditOutlined,
    EyeOutlined,
    FileOutlined,
} from '@ant-design/icons';
import { Collateral } from '../types/collateral.types';

const { Text } = Typography;

interface CollateralCardProps {
    collateral: Collateral;
    onView?: () => void;
    onEdit?: () => void;
    onDelete?: () => void;
    onManageDocuments?: () => void;
    deleting?: boolean;
}

const statusColor = (status?: string): string => {
    switch (status) {
        case 'AVAILABLE':
            return 'green';
        case 'PLEDGED':
            return 'blue';
        case 'UNDER_REVIEW':
            return 'gold';
        case 'RELEASED':
            return 'default';
        default:
            return 'default';
    }
};

const formatTzs = (value?: number): string => {
    if (value == null) return '—';
    return `TZS ${value.toLocaleString()}`;
};

/**
 * Card displaying a single collateral. Reused by the list and the loan
 * collateral-link panel.
 */
const CollateralCard: React.FC<CollateralCardProps> = ({
    collateral,
    onView,
    onEdit,
    onDelete,
    onManageDocuments,
    deleting,
}) => {
    return (
        <Card
            size="small"
            title={
                <Space>
                    <Text strong>{collateral.code}</Text>
                    <Tag color="blue">{collateral.collateralType}</Tag>
                    <Tag color={statusColor(collateral.status)}>{collateral.status}</Tag>
                </Space>
            }
            extra={
                <Space>
                    {onView && (
                        <Button size="small" icon={<EyeOutlined />} onClick={onView}>
                            View
                        </Button>
                    )}
                    {onManageDocuments && (
                        <Button
                            size="small"
                            icon={<FileOutlined />}
                            onClick={onManageDocuments}
                        >
                            Documents
                        </Button>
                    )}
                    {onEdit && (
                        <Button size="small" icon={<EditOutlined />} onClick={onEdit}>
                            Edit
                        </Button>
                    )}
                    {onDelete && (
                        <Popconfirm
                            title="Delete this collateral?"
                            description="Associated documents will also be removed."
                            okText="Delete"
                            okButtonProps={{ danger: true, loading: deleting }}
                            onConfirm={onDelete}
                        >
                            <Button size="small" danger icon={<DeleteOutlined />}>
                                Delete
                            </Button>
                        </Popconfirm>
                    )}
                </Space>
            }
        >
            <Descriptions size="small" column={2}>
                <Descriptions.Item label="Estimated Value">
                    {formatTzs(collateral.estimatedValue)}
                </Descriptions.Item>
                <Descriptions.Item label="Owner">
                    {collateral.ownerName || '—'}
                </Descriptions.Item>
                {collateral.location && (
                    <Descriptions.Item label="Location">
                        {collateral.location}
                    </Descriptions.Item>
                )}
                {collateral.registrationNo && (
                    <Descriptions.Item label="Registration">
                        {collateral.registrationNo}
                    </Descriptions.Item>
                )}
                {collateral.valuationDate && (
                    <Descriptions.Item label="Valued On">
                        {collateral.valuationDate}
                    </Descriptions.Item>
                )}
                {collateral.insuranceExpiry && (
                    <Descriptions.Item label="Insurance Expiry">
                        {collateral.insuranceExpiry}
                    </Descriptions.Item>
                )}
                {collateral.lienMark && (
                    <Descriptions.Item label="Lien">
                        <Tag color="purple">MARKED</Tag>
                        {collateral.lienExpiry && ` until ${collateral.lienExpiry}`}
                    </Descriptions.Item>
                )}
                {collateral.remarks && (
                    <Descriptions.Item label="Remarks" span={2}>
                        {collateral.remarks}
                    </Descriptions.Item>
                )}
            </Descriptions>
        </Card>
    );
};

export default CollateralCard;
